import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './modernized_app.css';

const RISK_LABELS = {
  good: 'Favorable',
  caution: 'Caution',
  restricted: 'Restricted',
  advisory: 'Advisory',
};

function formatTimestamp(value) {
  if (!value) return '--';
  let date;
  if (/^\d{12}$/.test(String(value))) {
    const raw = String(value);
    date = new Date(Date.UTC(
      Number(raw.slice(0, 4)),
      Number(raw.slice(4, 6)) - 1,
      Number(raw.slice(6, 8)),
      Number(raw.slice(8, 10)),
      Number(raw.slice(10, 12)),
    ));
  } else if (/^\d{8}T\d{4}Z$/.test(String(value))) {
    const raw = String(value);
    date = new Date(Date.UTC(
      Number(raw.slice(0, 4)),
      Number(raw.slice(4, 6)) - 1,
      Number(raw.slice(6, 8)),
      Number(raw.slice(9, 11)),
      Number(raw.slice(11, 13)),
    ));
  } else if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    if (numeric > 100000000000) {
      date = new Date(numeric);
    } else if (numeric > 1000000000) {
      date = new Date(numeric * 1000);
    } else {
      date = new Date(numeric);
    }
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function formatOptionalKt(value) {
  return value !== undefined && value !== null && value !== '' ? `${value} kt` : '--';
}

function formatZuluDayTime(value) {
  const match = String(value || '').match(/^(\d{2})(\d{2})(\d{2})?Z?$/);
  if (!match) return value || '--';
  const [, day, hour, minute = '00'] = match;
  return `day ${day} at ${hour}${minute}Z`;
}

function formatTafValidity(value) {
  const match = String(value || '').match(/^(\d{2})(\d{2})\/(\d{2})(\d{2})$/);
  if (!match) return value || '--';
  const [, startDay, startHour, endDay, endHour] = match;
  return `day ${startDay} ${startHour}00Z through day ${endDay} ${endHour}00Z`;
}

function describeWind(token) {
  const match = String(token || '').match(/^(VRB|\d{3})(\d{2,3})(G(\d{2,3}))?KT$/);
  if (!match) return null;
  const [, direction, speed, , gust] = match;
  const directionText = direction === 'VRB'
    ? 'variable direction'
    : `from ${Number(direction)} degrees`;
  return `Wind ${directionText} at ${Number(speed)} knots${gust ? `, gusting to ${Number(gust)} knots` : ''}`;
}

function describeVisibility(token) {
  const value = String(token || '');
  if (value === 'P6SM') return 'visibility greater than 6 statute miles';
  if (value === '6SM') return 'visibility 6 statute miles';
  if (/^\d+SM$/.test(value)) return `visibility ${value.replace('SM', '')} statute miles`;
  if (/^\d+\/\d+SM$/.test(value)) return `visibility ${value.replace('SM', '')} statute miles`;
  return null;
}

function describeCloud(token) {
  const match = String(token || '').match(/^(FEW|SCT|BKN|OVC|VV)(\d{3}|\/\/\/)(CB|TCU)?$/);
  if (!match) return null;
  const [, coverage, height, modifier] = match;
  const coverageMap = {
    FEW: 'few clouds',
    SCT: 'scattered clouds',
    BKN: 'broken clouds',
    OVC: 'overcast clouds',
    VV: 'vertical visibility',
  };
  const heightText = height === '///'
    ? 'height unknown'
    : `${Number(height) * 100} ft`;
  return `${coverageMap[coverage] || coverage} at ${heightText}${modifier ? ` ${modifier}` : ''}`;
}

function describeWeatherToken(token) {
  const value = String(token || '');
  const intensity = value.startsWith('-') ? 'light ' : value.startsWith('+') ? 'heavy ' : '';
  const cleaned = value.replace(/^[-+]/, '');
  const weatherMap = [
    ['VCTS', 'thunderstorms in the vicinity'],
    ['TSRA', 'thunderstorms with rain'],
    ['TS', 'thunderstorms'],
    ['SHRA', 'rain showers'],
    ['RA', 'rain'],
    ['DZ', 'drizzle'],
    ['SN', 'snow'],
    ['BR', 'mist'],
    ['FG', 'fog'],
    ['HZ', 'haze'],
    ['FU', 'smoke'],
    ['VA', 'volcanic ash'],
  ];
  const match = weatherMap.find(([code]) => cleaned.includes(code));
  return match ? `${intensity}${match[1]}` : null;
}

function describeTafSegment(tokens) {
  const descriptions = [];
  tokens.forEach(token => {
    const description = describeWind(token)
      || describeVisibility(token)
      || describeCloud(token)
      || describeWeatherToken(token);
    if (description) descriptions.push(description);
  });
  return descriptions.length ? `${descriptions.join('; ')}.` : 'No decoded details available for this period.';
}

function decodeTaf(raw) {
  const tokens = String(raw || '').trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return null;

  const normalized = tokens[0] === 'TAF' ? tokens.slice(1) : tokens;
  while (['AMD', 'COR'].includes(normalized[0])) normalized.shift();

  const station = normalized.find(token => /^[A-Z]{4}$/.test(token)) || '';
  const issued = normalized.find(token => /^\d{6}Z$/.test(token)) || '';
  const valid = normalized.find(token => /^\d{4}\/\d{4}$/.test(token)) || '';
  const validIndex = normalized.indexOf(valid);
  const periodTokens = validIndex >= 0 ? normalized.slice(validIndex + 1) : normalized.slice(3);

  const groups = [];
  let current = { label: 'Initial period', tokens: [] };
  periodTokens.forEach(token => {
    if (/^FM\d{6}$/.test(token)) {
      if (current.tokens.length) groups.push(current);
      current = { label: `From ${formatZuluDayTime(token.slice(2))}`, tokens: [] };
    } else if (['TEMPO', 'BECMG'].includes(token)) {
      if (current.tokens.length) groups.push(current);
      current = { label: token === 'TEMPO' ? 'Temporary conditions' : 'Becoming', tokens: [] };
    } else {
      current.tokens.push(token);
    }
  });
  if (current.tokens.length) groups.push(current);

  return {
    station: station || '--',
    issued: issued ? formatZuluDayTime(issued) : '--',
    valid: valid ? formatTafValidity(valid) : '--',
    groups,
  };
}

function WeatherMetric({ label, value, detail }) {
  return (
    <div className="weather-metric">
      <span>{label}</span>
      <strong>{value || '--'}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function formatPercent(value) {
  const number = asNumber(value);
  return `${Math.round(number)}%`;
}

function formatWindKt(value) {
  const number = asNumber(value);
  return `${Math.round(number)} kt`;
}

function HourlyOutlook({ periods }) {
  if (!periods?.length) {
    return <p className="weather-empty">No hourly NWS outlook is available for this location.</p>;
  }

  const maxWind = Math.max(...periods.map(period => asNumber(period.windSpeedKt)), 1);

  return (
    <div className="weather-hourly-strip">
      {periods.map((period, index) => {
        const precip = asNumber(period.precipChance);
        const wind = asNumber(period.windSpeedKt);
        return (
          <div key={`${period.startTime || period.name}-${index}`} className="weather-hourly-card">
            <span>{period.name || formatTimestamp(period.startTime)}</span>
            <div className="weather-hourly-main">
              <strong>{period.temperature ?? '--'}{period.temperatureUnit || 'F'}</strong>
              <em>{period.windSpeed || '--'} {period.windDirection || ''}</em>
            </div>
            <div className="weather-mini-bars">
              <div className="weather-mini-bar weather-mini-bar--precip" title={`Precip ${precip}%`}>
                <i style={{ height: `${Math.max(4, precip)}%` }} />
              </div>
              <div className="weather-mini-bar weather-mini-bar--wind" title={`Wind ${wind} kt`}>
                <i style={{ height: `${Math.max(4, (wind / maxWind) * 100)}%` }} />
              </div>
            </div>
            <div className="weather-mini-axis">
              <span className="is-precip">Precip<br /><b>{formatPercent(precip)}</b></span>
              <span className="is-wind">Wind<br /><b>{formatWindKt(wind)}</b></span>
            </div>
            <small>{period.shortForecast || '--'}</small>
          </div>
        );
      })}
    </div>
  );
}

export default function WeatherTab({
  api,
  user,
  selectedBase,
  latestWeather,
  onWeatherUpdate,
  isEnabled,
  isEntitlementLoading,
  onOpenUpgrade,
}) {
  const [weather, setWeather] = useState(latestWeather || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [supportGuidance, setSupportGuidance] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const exportRef = useRef(null);

  const loadWeather = async (force = false) => {
    if (!user?.email || !selectedBase) return;
    setLoading(true);
    setError('');
    setSupportGuidance('');
    try {
      const res = await api.get('/weather/summary', {
        params: {
          email: user.email,
          location: selectedBase,
          force: force || undefined,
        },
      });
      setAccessDenied(false);
      setWeather(res.data || null);
      setSupportGuidance(res.data?.available === false ? (res.data?.supportMessage || '') : '');
      if (typeof onWeatherUpdate === 'function') onWeatherUpdate(res.data || null);
    } catch (err) {
      console.error('Failed to load weather summary', err);
      const payload = err.response?.data || {};
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setWeather(null);
        if (typeof onWeatherUpdate === 'function') onWeatherUpdate(null);
        setError(payload.message || 'Upgrade required to use Weather for this location.');
        setSupportGuidance('Weather is turned off for this location. Use the Upgrade tab or contact your site leadership if access should be restored.');
        return;
      }
      setError(payload.message || 'Failed to load weather summary.');
      setSupportGuidance(payload.supportMessage || 'Please try again later, or use the Help tab to send a support ticket if this continues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setWeather(latestWeather || null);
    if (latestWeather) {
      setAccessDenied(false);
      setError('');
      setSupportGuidance('');
    }
  }, [latestWeather]);

  useEffect(() => {
    loadWeather(false);
  }, [api, user?.email, selectedBase, isEnabled]);

  const assessment = weather?.assessment || {};
  const metar = assessment.metar || {};
  const taf = assessment.taf || {};
  const decodedTaf = useMemo(() => decodeTaf(taf.raw), [taf.raw]);
  const risk = assessment.risk || 'unknown';
  const riskLabel = RISK_LABELS[risk] || 'Unavailable';
  const cache = weather?.cache || {};
  const weatherUnavailable = weather?.available === false;
  const stationLabel = weather?.station?.id
    ? `${weather.station.id}${weather.station.name ? ` - ${weather.station.name}` : ''}`
    : 'No station resolved';

  const summaryCards = useMemo(() => ([
    {
      label: 'Flying Condition',
      value: assessment.flyingCondition || riskLabel,
      detail: metar.flightCategory ? `METAR ${metar.flightCategory}` : 'Commander-level assessment',
    },
    {
      label: 'Maintenance Impact',
      value: assessment.maintenanceImpact || 'Weather data unavailable',
      detail: 'Ramp and outdoor work posture',
    },
    {
      label: 'Aviation Station',
      value: stationLabel,
      detail: `Cached ${cache.status || 'pending'}`,
    },
  ]), [assessment.flyingCondition, assessment.maintenanceImpact, cache.status, metar.flightCategory, riskLabel, stationLabel]);

  const exportToPDF = async () => {
    if (!exportRef.current) return;

    exportRef.current.classList.add('is-exporting-weather');
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#eef4fa',
        scale: 1.35,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let remainingHeight = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
      remainingHeight -= pageHeight;

      while (remainingHeight > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
        remainingHeight -= pageHeight;
      }

      const safeBase = String(selectedBase || 'location').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'location';
      pdf.save(`weather_${safeBase}.pdf`);
    } finally {
      exportRef.current?.classList.remove('is-exporting-weather');
    }
  };

  if (isEntitlementLoading) {
    return (
      <div className="weather-tab">
        <section className="reports-panel launch-advisor-access-card">
          <div>
            <div className="reports-eyebrow">Premium Weather Dashboard</div>
            <h2>Checking Weather Access</h2>
            <p>Confirming the subscription entitlement for this location.</p>
          </div>
        </section>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="weather-tab">
        <section className="reports-panel launch-advisor-access-card weather-access-card is-locked">
          <div>
            <div className="reports-eyebrow">Premium Add-On</div>
            <h2>Upgrade to Access Weather</h2>
            <p>
              {error || 'Weather is turned off for this location.'}
            </p>
            <p>
              Weather adds live NWS and NOAA Aviation Weather Center data, commander-level flying-condition summaries,
              maintenance advisories, and PDF-ready weather reports for the selected location.
            </p>
            {supportGuidance ? <p>{supportGuidance}</p> : null}
            <button type="button" onClick={onOpenUpgrade}>
              Upgrade to Access
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <section className="weather-tab" ref={exportRef}>
      <div className="weather-tab__header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Weather</h2>
          <p className="weather-tab__description">
            Commander-level flying conditions and maintenance weather posture for {selectedBase}.
          </p>
        </div>
        <div className="weather-tab__actions">
          <span>Hourly cache</span>
          <span className="weather-tab__source-pill">Sources: NWS + NOAA AWC</span>
          <span className="weather-tab__last-run">Last report {formatTimestamp(cache.fetchedAt || weather?.generatedAt)}</span>
          <button type="button" onClick={exportToPDF}>Export PDF</button>
          <button type="button" onClick={() => loadWeather(true)} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="weather-alert weather-alert--error">
          <strong>{error}</strong>
          {supportGuidance ? <span>{supportGuidance}</span> : null}
        </div>
      ) : null}
      {weatherUnavailable ? (
        <div className="weather-alert weather-alert--error">
          <strong>{weather.message || 'Weather is not available for this location.'}</strong>
          {weather.supportMessage ? <span>{weather.supportMessage}</span> : null}
        </div>
      ) : null}
      {weather?.stale ? (
        <div className="weather-alert weather-alert--warning">
          <strong>Showing stale weather data because the latest upstream refresh failed.</strong>
          <span>{cache.warning || 'Please try again later, or use the Help tab to send a support ticket if this continues.'}</span>
        </div>
      ) : null}
      {assessment.banner?.active ? (
        <div className="weather-alert weather-alert--critical">
          <strong>{assessment.banner.title}</strong>
          <span>{assessment.banner.body}</span>
        </div>
      ) : null}

      <div className={`weather-risk-panel is-${risk}`}>
        <div>
          <span>Operational Risk</span>
          <strong>{riskLabel}</strong>
        </div>
        <div className="weather-risk-panel__bar" aria-hidden="true">
          <i />
        </div>
      </div>

      <div className="weather-summary-grid">
        {summaryCards.map(card => (
          <WeatherMetric key={card.label} label={card.label} value={card.value} detail={card.detail} />
        ))}
      </div>

      <div className="weather-dashboard-grid">
        <section className="weather-panel">
          <h3>Current METAR</h3>
          <div className="weather-metar-grid">
            <WeatherMetric label="Category" value={metar.flightCategory || '--'} />
            <WeatherMetric label="Wind" value={formatOptionalKt(metar.windSpeedKt)} detail={metar.windDirection ? `${metar.windDirection} deg` : ''} />
            <WeatherMetric label="Gust" value={formatOptionalKt(metar.windGustKt)} />
            <WeatherMetric label="Visibility" value={metar.visibilitySm ? `${metar.visibilitySm} SM` : '--'} />
            <WeatherMetric label="Temp" value={metar.temperatureC !== undefined && metar.temperatureC !== null ? `${metar.temperatureC} C` : '--'} />
            <WeatherMetric label="Dew Point" value={metar.dewpointC !== undefined && metar.dewpointC !== null ? `${metar.dewpointC} C` : '--'} />
          </div>
          <p className="weather-raw">{metar.raw || 'No current METAR available.'}</p>
          <small>Observed {formatTimestamp(metar.observedAt)}</small>
        </section>

        <section className="weather-panel">
          <h3>TAF</h3>
          {decodedTaf ? (
            <div className="weather-decoded-taf">
              <div className="weather-decoded-taf__grid">
                <WeatherMetric label="Station" value={decodedTaf.station} />
                <WeatherMetric label="Issued" value={decodedTaf.issued} />
                <WeatherMetric label="Valid" value={decodedTaf.valid} />
              </div>
              <ul className="weather-decoded-list">
                {decodedTaf.groups.map((group, index) => (
                  <li key={`${group.label}-${index}`}>
                    <strong>{group.label}:</strong> {describeTafSegment(group.tokens)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="weather-raw">{taf.raw || 'No TAF available for the resolved station.'}</p>
          <small>
            Issued {formatTimestamp(taf.issuedAt)} | Valid {formatTimestamp(taf.validFrom)} to {formatTimestamp(taf.validTo)}
          </small>
        </section>
      </div>

      <section className="weather-panel">
        <div className="weather-panel__header">
          <h3>12-Hour Ground Outlook</h3>
          <p>Blue = precipitation chance. Orange = wind speed, scaled to the strongest wind shown.</p>
        </div>
        <HourlyOutlook periods={weather?.hourlyOutlook || []} />
      </section>

      <div className="weather-dashboard-grid">
        <section className="weather-panel">
          <h3>Commander Notes</h3>
          <ul className="weather-reason-list">
            {(assessment.reasons || []).map((reason, index) => (
              <li key={`${reason}-${index}`}>{reason}</li>
            ))}
          </ul>
        </section>

        <section className="weather-panel">
          <h3>Active NWS Alerts</h3>
          {assessment.alerts?.length ? (
            <div className="weather-alert-list">
              {assessment.alerts.map((alert, index) => (
                <article key={alert.id || `${alert.event}-${index}`}>
                  <strong>{alert.event || 'Weather Alert'}</strong>
                  <p>{alert.headline || 'No headline provided.'}</p>
                  <small>{alert.severity || 'Unknown severity'} | Expires {formatTimestamp(alert.expires)}</small>
                </article>
              ))}
            </div>
          ) : (
            <p className="weather-empty">No active NWS alerts for this location.</p>
          )}
        </section>
      </div>

      <p className="weather-source-note">
        Sources: NWS api.weather.gov and NOAA Aviation Weather Center. Last fetch {formatTimestamp(cache.fetchedAt)}.
      </p>
    </section>
  );
}
